import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronRight, Plus, Search, Trash2, X } from "lucide-react";
import { QuickReply, QuickReplyDirection } from "../types";
import "./QuickRepliesPanel.css";

type CatalogStep = "directions" | "topics" | "replies";

interface QuickRepliesPanelProps {
  directions: QuickReplyDirection[];
  replies: QuickReply[];
  selectedChatId: string | null;
  onClose: () => void;
  onCreateTopic?: (directionId: string, name: string) => Promise<string>;
  onUpdateTopic?: (id: string, name: string) => Promise<void>;
  onDeleteTopic?: (id: string) => Promise<void>;
  onCreateReply: (topicId: string, text: string) => Promise<void>;
  onUpdateReply: (id: string, text: string) => Promise<void>;
  onDeleteReply: (id: string) => Promise<void>;
  onInsertReply: (text: string) => void;
}

const stepIndex: Record<CatalogStep, number> = {
  directions: 0,
  topics: 1,
  replies: 2,
};

export const pluralizeRu = (
  value: number,
  one: string,
  few: string,
  many: string,
) => {
  const normalized = Math.abs(value) % 100;
  const lastDigit = normalized % 10;
  if (normalized >= 11 && normalized <= 14) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export function QuickRepliesPanel({
  directions,
  replies,
  selectedChatId,
  onClose,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onCreateReply,
  onUpdateReply,
  onDeleteReply,
  onInsertReply,
}: QuickRepliesPanelProps) {
  const [step, setStep] = useState<CatalogStep>("directions");
  const [selectedDirectionId, setSelectedDirectionId] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [createdTopicName, setCreatedTopicName] = useState("");
  const [query, setQuery] = useState("");
  const [topicQuery, setTopicQuery] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [editing, setEditing] = useState<QuickReply | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const activeHeadingRef = useRef<HTMLHeadingElement>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const selectedDirection = directions.find(
    (direction) => direction.id === selectedDirectionId,
  );
  const selectedTopic = selectedDirection?.topics.find(
    (topic) => topic.id === selectedTopicId,
  );
  const topicName = selectedTopic?.name || createdTopicName;
  const visibleReplies = useMemo(
    () =>
      replies.filter(
        (reply) =>
          reply.topic_id === selectedTopicId &&
          reply.text.toLowerCase().includes(query.trim().toLowerCase()),
      ),
    [query, replies, selectedTopicId],
  );
  const visibleTopics = useMemo(() => {
    const normalizedQuery = topicQuery.trim().toLowerCase();
    return (selectedDirection?.topics || []).filter((topic) =>
      topic.name.toLowerCase().includes(normalizedQuery),
    );
  }, [selectedDirection, topicQuery]);
  const screenClassName = (screen: CatalogStep) => {
    const difference = stepIndex[screen] - stepIndex[step];
    return `quick-catalog-screen ${
      difference === 0
        ? "is-active"
        : difference < 0
          ? "is-before"
          : "is-after"
    }`;
  };

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      viewportRef.current?.scrollTo({ left: 0, top: 0 });
      activeHeadingRef.current?.focus({ preventScroll: true });
      viewportRef.current?.scrollTo({ left: 0, top: 0 });
    });
    return () => cancelAnimationFrame(frame);
  }, [step]);

  const goToTopics = (directionId: string) => {
    setSelectedDirectionId(directionId);
    setSelectedTopicId("");
    setCreatedTopicName("");
    setNewTopic("");
    setTopicQuery("");
    setError("");
    setStep("topics");
  };

  const goToReplies = (topicId: string, name = "") => {
    setSelectedTopicId(topicId);
    setCreatedTopicName(name);
    setQuery("");
    setEditing(null);
    setDraft("");
    setError("");
    setStep("replies");
  };

  const goBack = () => {
    setError("");
    setEditing(null);
    setDraft("");
    if (step === "replies") {
      setStep("topics");
      setSelectedTopicId("");
      setCreatedTopicName("");
      setQuery("");
    } else if (step === "topics") {
      setStep("directions");
      setSelectedDirectionId("");
      setTopicQuery("");
    }
  };

  const perform = async (operation: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await operation();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось сохранить изменения");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="quick-overlay" onClick={onClose}>
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-replies-title"
        className="quick-panel quick-catalog-panel"
        style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
        onClick={(event) => event.stopPropagation()}
      >
        <header
          className="quick-panel-drag-handle"
          onPointerDown={(event) => {
            if (
              window.innerWidth <= 700 ||
              (event.target as Element).closest("button")
            )
              return;
            drag.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              originX: position.x,
              originY: position.y,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const current = drag.current;
            const panel = panelRef.current;
            if (!current || current.pointerId !== event.pointerId || !panel)
              return;
            const rect = panel.getBoundingClientRect();
            const nextX = current.originX + event.clientX - current.startX;
            const nextY = current.originY + event.clientY - current.startY;
            setPosition({
              x: Math.max(
                nextX - rect.left,
                Math.min(nextX, window.innerWidth - rect.right + nextX),
              ),
              y: Math.max(
                nextY - rect.top,
                Math.min(nextY, window.innerHeight - rect.bottom + nextY),
              ),
            });
          }}
          onPointerUp={(event) => {
            if (drag.current?.pointerId === event.pointerId) drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
        >
          <div>
            <h3 id="quick-replies-title">Быстрые ответы</h3>
            <p>
              {selectedChatId
                ? "Нажмите на ответ, чтобы вставить в черновик"
                : "Сначала выберите чат"}
            </p>
          </div>
          <button
            className="quick-close-button"
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X size={20} />
          </button>
        </header>

        <div className="quick-catalog-viewport" ref={viewportRef}>
          <div
            className="quick-catalog-track"
          >
            <section className={screenClassName("directions")} aria-hidden={step !== "directions"}>
              <div className="quick-screen-heading">
                <div>
                  <h4 ref={step === "directions" ? activeHeadingRef : undefined} tabIndex={-1}>
                    Выберите направление
                  </h4>
                  <p>Ответы распределены по направлениям обучения</p>
                </div>
              </div>
              <div className="quick-direction-cards">
                {directions.map((direction) => (
                  <button
                    key={direction.id}
                    type="button"
                    aria-label={direction.name}
                    onClick={() => goToTopics(direction.id)}
                    tabIndex={step === "directions" ? 0 : -1}
                  >
                    <span>{direction.name}</span>
                    <small>
                      {direction.topics.length}{" "}
                      {pluralizeRu(
                        direction.topics.length,
                        "тема",
                        "темы",
                        "тем",
                      )}
                    </small>
                    <ChevronRight size={19} />
                  </button>
                ))}
              </div>
            </section>

            <section className={screenClassName("topics")} aria-hidden={step !== "topics"}>
              <div className="quick-screen-heading">
                <button
                  className="quick-back-button"
                  type="button"
                  onClick={goBack}
                  tabIndex={step === "topics" ? 0 : -1}
                  aria-label="Назад к направлениям"
                >
                  <ArrowLeft size={19} />
                </button>
                <div>
                  <h4 ref={step === "topics" ? activeHeadingRef : undefined} tabIndex={-1}>
                    {selectedDirection?.name || "Темы"}
                  </h4>
                  <p>Выберите тему или создайте новую</p>
                </div>
              </div>
              <div className="quick-search quick-topic-search">
                <Search size={16} />
                <input
                  value={topicQuery}
                  onChange={(event) => setTopicQuery(event.target.value)}
                  placeholder="Поиск по темам"
                  tabIndex={step === "topics" ? 0 : -1}
                />
              </div>
              <div className="quick-topic-cards">
                {visibleTopics.map((topic) => {
                  const replyCount = replies.filter(
                    (reply) => reply.topic_id === topic.id,
                  ).length;
                  return (
                  <button
                    key={topic.id}
                    type="button"
                    aria-label={topic.name}
                    onClick={() => goToReplies(topic.id)}
                    tabIndex={step === "topics" ? 0 : -1}
                  >
                    <span>{topic.name}</span>
                    <small>
                      {replyCount}{" "}
                      {pluralizeRu(replyCount, "ответ", "ответа", "ответов")}
                    </small>
                    <ChevronRight size={19} />
                  </button>
                  );
                })}
                {selectedDirection && selectedDirection.topics.length === 0 && (
                  <div className="quick-step-empty">
                    <strong>Тем пока нет</strong>
                    <span>Создайте первую тему для этого направления</span>
                  </div>
                )}
                {selectedDirection &&
                  selectedDirection.topics.length > 0 &&
                  visibleTopics.length === 0 && (
                    <div className="quick-step-empty">
                      <strong>Темы не найдены</strong>
                      <span>Попробуйте изменить поисковый запрос</span>
                    </div>
                  )}
              </div>
              <div className="quick-catalog-topic-create">
                <input
                  maxLength={100}
                  value={newTopic}
                  onChange={(event) => setNewTopic(event.target.value)}
                  placeholder="Новая тема"
                  tabIndex={step === "topics" ? 0 : -1}
                />
                <button
                  type="button"
                  aria-label="Добавить тему"
                  disabled={busy || !newTopic.trim() || !onCreateTopic}
                  tabIndex={step === "topics" ? 0 : -1}
                  onClick={() =>
                    void perform(async () => {
                      if (!selectedDirection || !onCreateTopic) return;
                      const name = newTopic.trim();
                      const id = await onCreateTopic(selectedDirection.id, name);
                      setNewTopic("");
                      goToReplies(id, name);
                    })
                  }
                >
                  <Plus size={17} />
                </button>
              </div>
            </section>

            <section className={screenClassName("replies")} aria-hidden={step !== "replies"}>
              <div className="quick-screen-heading quick-replies-heading">
                <button
                  className="quick-back-button"
                  type="button"
                  onClick={goBack}
                  tabIndex={step === "replies" ? 0 : -1}
                  aria-label="Назад к темам"
                >
                  <ArrowLeft size={19} />
                </button>
                <div>
                  <h4 ref={step === "replies" ? activeHeadingRef : undefined} tabIndex={-1}>
                    {topicName || "Ответы"}
                  </h4>
                  <p>{selectedDirection?.name}</p>
                </div>
                <div className="quick-topic-manage">
                  <button
                    type="button"
                    disabled={busy || !selectedTopic || !onUpdateTopic}
                    tabIndex={step === "replies" ? 0 : -1}
                    onClick={() => {
                      if (!selectedTopic || !onUpdateTopic) return;
                      const name = window.prompt("Название темы", selectedTopic.name);
                      if (name?.trim())
                        void perform(() => onUpdateTopic(selectedTopic.id, name.trim()));
                    }}
                  >
                    Переименовать
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={busy || !onDeleteTopic}
                    tabIndex={step === "replies" ? 0 : -1}
                    onClick={() => {
                      if (!onDeleteTopic || !window.confirm("Удалить тему и все ответы в ней?")) return;
                      void perform(async () => {
                        await onDeleteTopic(selectedTopicId);
                        goBack();
                      });
                    }}
                  >
                    <Trash2 size={14} />
                    Удалить
                  </button>
                </div>
              </div>

              <div className="quick-search">
                <Search size={16} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={`Поиск в теме «${topicName || ""}»`}
                  tabIndex={step === "replies" ? 0 : -1}
                />
              </div>

              {editing && (
                <div className="quick-editor">
                  <textarea
                    maxLength={1000}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    autoFocus
                    placeholder="Текст быстрого ответа"
                  />
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(null);
                        setDraft("");
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      disabled={busy || !draft.trim()}
                      onClick={() =>
                        void perform(async () => {
                          if (editing.id) await onUpdateReply(editing.id, draft.trim());
                          else await onCreateReply(selectedTopicId, draft.trim());
                          setEditing(null);
                          setDraft("");
                        })
                      }
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              )}

              <div className="quick-items quick-step-items">
                {visibleReplies.map((reply) => (
                  <article key={reply.id}>
                    <button
                      type="button"
                      className="quick-card-insert"
                      disabled={!selectedChatId}
                      tabIndex={step === "replies" ? 0 : -1}
                      aria-label={reply.text}
                      onClick={() => {
                        onInsertReply(reply.text);
                        onClose();
                      }}
                    />
                    <div className="quick-text">
                      {reply.text}
                    </div>
                    <div className="quick-item-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(reply);
                          setDraft(reply.text);
                        }}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        aria-label="Удалить ответ"
                        disabled={busy}
                        onClick={() => void perform(() => onDeleteReply(reply.id))}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                ))}
                {visibleReplies.length === 0 && (
                  <div className="quick-step-empty">
                    <strong>{query ? "Ничего не найдено" : "В этой теме пока нет ответов"}</strong>
                    {!query && <span>Добавьте первый быстрый ответ</span>}
                  </div>
                )}
              </div>
              <button
                className="quick-add"
                type="button"
                disabled={busy || !selectedTopicId}
                tabIndex={step === "replies" ? 0 : -1}
                onClick={() => {
                  setEditing({
                    id: "",
                    text: "",
                    topic_id: selectedTopicId,
                    sort_order: replies.length,
                  });
                  setDraft("");
                }}
              >
                <Plus size={17} />
                Добавить ответ
              </button>
            </section>
          </div>
        </div>
        {error && <div className="quick-catalog-error">{error}</div>}
      </section>
    </div>
  );
}
